import { CSSProperties, ReactNode } from 'react';

interface Props {
  onClick?: () => void;
  label: string;
  icon?: ReactNode;
  variant?: 'default' | 'primary' | 'danger' | 'ghost';
  size?: 'md' | 'lg';
  disabled?: boolean;
  style?: CSSProperties;
}

const baseStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  borderRadius: 'var(--radius)',
  fontWeight: 600,
  padding: '0 18px',
  transition: 'transform 60ms ease, opacity 100ms ease',
  userSelect: 'none',
  WebkitUserSelect: 'none',
  whiteSpace: 'nowrap',
};

export default function IconButton({
  onClick,
  label,
  icon,
  variant = 'default',
  size = 'md',
  disabled,
  style,
}: Props) {
  const heights = { md: 56, lg: 64 };
  const fontSize = size === 'lg' ? 18 : 16;

  const variants: Record<NonNullable<Props['variant']>, CSSProperties> = {
    default: {
      background: 'var(--surface-strong)',
      color: 'var(--text)',
      backdropFilter: 'blur(18px)',
      WebkitBackdropFilter: 'blur(18px)',
      border: '1px solid var(--hairline)',
    },
    primary: {
      background: 'var(--accent-strong)',
      color: 'white',
    },
    danger: {
      background: 'rgba(255, 69, 58, 0.18)',
      color: 'var(--danger)',
      border: '1px solid rgba(255, 69, 58, 0.32)',
    },
    ghost: {
      background: 'transparent',
      color: 'var(--text)',
    },
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      style={{
        ...baseStyle,
        ...variants[variant],
        height: heights[size],
        minWidth: heights[size],
        fontSize,
        ...style,
      }}
      onTouchStart={(e) => {
        (e.currentTarget as HTMLElement).style.transform = 'scale(0.96)';
      }}
      onTouchEnd={(e) => {
        (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
      }}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
